import React, { useEffect, useMemo, useState } from 'react';
import { withTranslation } from 'react-i18next';
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Spinner,
  UncontrolledPopover,
  PopoverHeader,
  PopoverBody,
  Modal,
  ModalHeader,
  ModalBody,
} from 'reactstrap';
import Breadcrumb from '../../Components/Common/BreadCrumb';
import TableContainer from '../../Components/Common/TableContainer';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { getRolesPaginated, getUsers } from '../../slices/thunks';
import {
  formatCurrency,
  formatDate,
  classifyPermissions,
} from '../../helpers/format';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { first } from 'lodash';
import { Link } from 'react-router-dom';

const Permission = (props) => {
  document.title = 'Permissões | Opencashback';

  const [modalPermissions, setModalPermissions] = useState({
    isOpen: false,
    roleName: '',
    classifications: [],
  });

  const toggleModalPermissions = () => {
    setModalPermissions({ ...modalPermissions, isOpen: !modalPermissions.isOpen });
  };

  const [createUserModal, setCreateUserModal] = useState(false);
  const [editUserModal, setEditUserModal] = useState(false);
  const [editUser, setEditUser] = useState({});

  const toggleCreateUser = () => {
    setCreateUserModal(!createUserModal);
  };

  const toggleEditUser = () => {
    setEditUserModal(!editUserModal);
  };

  const dispatch = useDispatch();
  const rolesSelector = createSelector(
    (state) => state.User.roles,
    (roles) => roles
  );

  const metaSeletor = createSelector(
    (state) => state.User.rolesMeta,
    (meta) => meta
  );

  const isLoadingSelector = createSelector(
    (state) => state.User.isLoadingRoles,
    (isLoading) => isLoading
  );

  const roles = useSelector(rolesSelector);
  const meta = useSelector(metaSeletor);
  const isLoading = useSelector(isLoadingSelector);

  const [page, setPage] = useState(1);

  const getData = () => {
    const params = {
      page,
    };

    dispatch(getRolesPaginated(params));
  };

  useEffect(() => {
    getData();
  }, [dispatch, page]);

  const getStatusBadgeClasses = (status) => {
    switch (status) {
      case 'active':
        return 'bg-success-subtle text-success';
      case 'inactive':
        return 'bg-warning-subtle text-warning';
      default:
        return 'bg-danger-subtle text-danger';
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: props.t('name'),
        accessor: 'name',
        filterable: false,
        Cell: (info) => info.row.original.name,
      },
      {
        Header: props.t('created-at'),
        accessor: 'created_at',
        filterable: false,
        Cell: (info) => formatDate(info.row.original.created_at),
      },
      {
        Header: props.t('description'),
        accessor: 'description',
        filterable: false,
        Cell: (info) => info.row.original.description || '-',
      },
      {
        Header: props.t('actions'),
        accessor: 'permissions',
        filterable: false,
        Cell: (info) => {
          const permissions = info.row.original.permissions || [];
          const classifications = classifyPermissions(permissions, props.t);
          const roleName = info.row.original.name;

          if (permissions.length === 0) return '-';

          return (
            <button
              type="button"
              className="btn btn-sm btn-soft-primary"
              onClick={() =>
                setModalPermissions({
                  isOpen: true,
                  roleName,
                  classifications,
                })
              }
            >
              <i className="ri-shield-user-line align-bottom me-1"></i>
              {classifications.length} {props.t('modules.label') || 'Módulos'}
            </button>
          );
        },
      },
    ],
    [props]
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumb title="Permissões" pageTitle="Configurações" />
          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader>
                  <Row>
                    <Col sm={1}>{isLoading && <Spinner color="primary" />}</Col>
                    <div className="col-sm-auto ms-auto">
                      <div className="hstack gap-2">
                        {/* <button type="button" className="btn btn-secondary" onClick={() => {}}>
                          <i className="ri-filter-3-line align-bottom me-1"></i>{" "}
                          {props.t('filters')}
                        </button> */}
                        {/* <button
                          type="button"
                          className="btn btn-success add-btn"
                          id="create-btn"
                          onClick={toggleCreateUser}
                        >
                          <i className="ri-add-line align-bottom me-1"></i>
                          {props.t('new-user')}
                        </button> */}
                      </div>
                    </div>
                  </Row>
                </CardHeader>
                <CardBody className="pt-0">
                  <div>
                    <TableContainer
                      columns={columns}
                      data={roles || []}
                      activePage={page}
                      pageSize={meta?.take || 10}
                      totalPages={meta?.pages || 0}
                      count={meta?.total || 0}
                      setPage={setPage}
                      className="custom-header-css"
                      divClass="table-responsive table-card mb-0"
                      tableClass="align-middle table-nowrap"
                      theadClass="table-light"
                    />
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>

        <Modal
          isOpen={modalPermissions.isOpen}
          toggle={toggleModalPermissions}
          centered
        >
          <ModalHeader toggle={toggleModalPermissions}>
            {props.t('view-permissions')} - {modalPermissions.roleName}
          </ModalHeader>
          <ModalBody>
            <div className="table-responsive">
              <table className="table align-middle table-nowrap mb-0">
                <thead className="table-light">
                  <tr>
                    <th>{props.t('module')}</th>
                    <th>{props.t('access')}</th>
                  </tr>
                </thead>
                <tbody>
                  {modalPermissions.classifications.map((item, index) => (
                    <tr key={index}>
                      <td className="fw-bold">{item.friendlySubject}</td>
                      <td>
                        <span
                          className={`badge ${
                            item.classificationKey === 'total'
                              ? 'bg-success'
                              : 'bg-warning text-dark'
                          }`}
                          style={{ fontSize: '0.85rem' }}
                        >
                          {item.classification}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </ModalBody>
        </Modal>
      </div>
    </React.Fragment>
  );
};

export default withTranslation()(Permission);
